import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, TrendingUp, CheckCircle, Star } from 'lucide-react';
import { testimonials, caseStudies, consultingServices, howItWorks } from '../mock/data';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import './Home.css';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="display-huge">
            Upgrade Your Business to Business Class
          </h1>
          <p className="body-large hero-subheadline">
            We help founders build high-performance businesses with better systems, 
            better customer experience, and scalable growth strategies.
          </p>
          <div className="hero-cta-group">
            <Link to="/consulting">
              <Button className="btn-primary">
                <span>Explore Consulting</span>
                <ArrowRight size={20} />
              </Button>
            </Link>
            <Link to="/community">
              <Button className="btn-secondary">
                <span>Join the Community</span>
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="about-preview-section">
        <div className="section-content">
          <div className="about-preview-grid">
            <div className="about-preview-text">
              <h2 className="display-medium">Business Class Is a Standard, Not a Luxury</h2>
              <p className="body-medium">
                Most businesses operate in "economy mode"—unstructured, reactive, inconsistent. 
                We help you upgrade to business class: structured systems, premium customer experience, 
                and predictable growth.
              </p>
              <Link to="/about">
                <Button className="btn-primary mt-8">
                  <span>Learn Our Story</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
            <div className="about-preview-stats">
              <div className="stat-card">
                <div className="stat-number">250+</div>
                <div className="stat-label">Businesses Transformed</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">3.5x</div>
                <div className="stat-label">Average Revenue Growth</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">18</div>
                <div className="stat-label">Months Average Scale Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Services Preview */}
      <section className="services-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="display-medium">How We Help You Scale</h2>
            <p className="body-medium section-subtitle">
              Systematic solutions for sustainable growth
            </p>
          </div>
          <div className="services-grid">
            {consultingServices.map(service => (
              <Card key={service.id} className="service-card">
                <div className="service-card-content">
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium service-description">{service.description}</p>
                  <div className="service-outcomes">
                    {service.outcomes.slice(0, 2).map((outcome, idx) => (
                      <div key={idx} className="outcome-item">
                        <CheckCircle size={18} className="outcome-icon" />
                        <span className="body-small">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/consulting">
              <Button className="btn-primary">
                <span>View All Services</span>
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Community Overview */}
      <section className="community-overview-section">
        <div className="section-content">
          <div className="community-overview-grid">
            <div className="community-overview-text">
              <h2 className="display-medium">Join the Business Class Community</h2>
              <p className="body-medium">
                Connect with serious founders building exceptional businesses. No fluff, only value. 
                Growth-focused environment for entrepreneurs scaling from ₹10L to ₹10Cr+.
              </p>
              <div className="community-features">
                <div className="feature-item">
                  <Users size={24} className="feature-icon" />
                  <div>
                    <div className="heading-3">Peer Masterminds</div>
                    <div className="body-small text-muted">Weekly sessions with fellow founders</div>
                  </div>
                </div>
                <div className="feature-item">
                  <Target size={24} className="feature-icon" />
                  <div>
                    <div className="heading-3">Growth Frameworks</div>
                    <div className="body-small text-muted">Proven systems and templates</div>
                  </div>
                </div>
                <div className="feature-item">
                  <TrendingUp size={24} className="feature-icon" />
                  <div>
                    <div className="heading-3">Strategy Calls</div>
                    <div className="body-small text-muted">Regular coaching and guidance</div>
                  </div>
                </div>
              </div>
              <Link to="/community">
                <Button className="btn-primary mt-8">
                  <span>Explore Membership Tiers</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="display-medium">Our Approach</h2>
            <p className="body-medium section-subtitle">
              Simple, systematic, effective
            </p>
          </div>
          <div className="how-it-works-grid">
            {howItWorks.map(step => (
              <div key={step.step} className="step-card">
                <div className="step-number">0{step.step}</div>
                <h3 className="heading-2">{step.title}</h3>
                <p className="body-medium">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="case-studies-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="display-medium">Results That Matter</h2>
            <p className="body-medium section-subtitle">
              Real businesses, real transformations
            </p>
          </div>
          <div className="case-studies-grid">
            {caseStudies.map(study => (
              <Card key={study.id} className="case-study-card">
                <div className="case-study-content">
                  <div className="case-study-client">{study.client}</div>
                  <div className="case-study-detail">
                    <div className="detail-label">Challenge</div>
                    <div className="body-small">{study.challenge}</div>
                  </div>
                  <div className="case-study-detail">
                    <div className="detail-label">Solution</div>
                    <div className="body-small">{study.solution}</div>
                  </div>
                  <div className="case-study-result">
                    <div className="result-text">{study.result}</div>
                    <div className="result-timeline">{study.timeline}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="display-medium">What Founders Say</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map(testimonial => (
              <Card key={testimonial.id} className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} fill="#00FFD1" color="#00FFD1" />
                    ))}
                  </div>
                  <p className="body-medium testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="section-content">
          <div className="final-cta-content">
            <h2 className="display-large">Ready to Upgrade?</h2>
            <p className="body-large">
              Stop operating in economy mode. Build a business that runs with excellence.
            </p>
            <div className="final-cta-buttons">
              <Link to="/contact">
                <Button className="btn-primary">
                  <span>Book a Strategy Call</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/community">
                <Button className="btn-secondary">
                  <span>Join the Community</span>
                  <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
